import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, forwardRef, input } from "@angular/core";
import { InputFormWrapper } from "../wrapper.interface";
import { DropdownConfig } from "./dropdown.config";
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { Select, SelectModule} from "primeng/select";
import { GroupedOption, Option } from "src/util/types/global";
import { PrimeTemplate, SharedModule } from "primeng/api";
import { V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { TranslateModule } from "@ngx-translate/core";

@Component({
    selector:      'v-dropdown',
    templateUrl:   './dropdown.component.haml',
    styleUrls:     ['./dropdown.component.sass', '../input.style.sass'],
    encapsulation: ViewEncapsulation.None,
    providers:     [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VDropdownComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VDropdownComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VDropdownComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [CommonModule, SelectModule, ReactiveFormsModule, SharedModule, TranslateModule]
})
export class VDropdownComponent<T = string>  extends InputFormWrapper<DropdownConfig, T> {
    protected readonly _baseConfig: DropdownConfig = DropdownConfig.baseTheme.Default;
    options = input<(Option<T> | GroupedOption<T>)[]>();
    filterBy = input<string>();
    panelStyleClass = input<string>();

    cLabel = computed(() => {
        const label = this.config().optionLabel;
        const obj = this.options()?.find(x => x.value)?.value as object;
        if (!label || (obj && !(typeof obj == 'object' && label in obj))) return label ?? 'label';
        return `value.${label}`;
    });
    cValue = computed(() => {
        const value = this.config().optionValue;
        const obj = this.options()?.find(x => x.value)?.value as object;
        if (!value || (obj && !(typeof obj == 'object' && value in obj))) return value ?? 'value';
        return `value.${value}`;
    });

    cFilter = computed(() => {
        let filter = this.filterBy();
        if (filter === "") filter = "label"; // Default filter by label if explicit filter is provided, but attribute is set
        const obj = this.options()?.find(x => x.value)?.value as object;
        if (!filter || (obj && !(typeof obj == 'object' && filter in obj))) return filter;
        return `value.${filter}`;
    });

    cGroup = computed(() => {
        const group = this.config().optionGroup ?? 'items';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const obj = this.options().find(x => group in x && Array.isArray((x as any)[group]));
        return !!obj;
    });
    ngAfterViewInit() { // Prime Update 19 reassigns templates to undefined after initialization
        this.gcTemplateRef().forEach(x => this.assignTemplate(x));
        this.primeTemplateRef().forEach(x => this.assignTemplate(x));
    }

    protected assignTemplate(x: PrimeTemplate) {
        const el = this.wrappedComponent as Select;
        switch(x.name) {
            case 'header':
                el.headerTemplate = el._headerTemplate = x.template;
                break;
            case 'footer':
                el.footerTemplate = el._footerTemplate = x.template;
                break;
            case 'filter':
                el.filterTemplate = el._filterTemplate = x.template;
                break;
            case 'item':
                el.itemTemplate = el._itemTemplate = x.template;
                break;
            case 'group':
                el.groupTemplate = el._groupTemplate = x.template;
                break;
            case 'selectedItem':
                el.selectedItemTemplate = el._selectedItemTemplate = x.template;
                break;
        }
    }

    clear(): void {
        const component = (this.wrappedComponent as Select);
        component.clear();
    }
}